import { URLS } from "api/utils/baseUrls";
import { OPEKEPE_SYNC_SCOPE } from "constants/opekepe";
import axiosInstance from "../utils/axiosInstance";
import { OpekepeDto } from "../dtos/OpekepeDto";
import { SubsidyReportDto } from "api/dtos/SubsidyReportDto";
import { constructSearchParams } from "utils/constructSearchParams";
import { InflowOutflowDto } from "api/dtos/InflowOutflowDto";

const syncAccount = async ({ code, scope }) => {
  try {
    const response = await axiosInstance.post(`${URLS.opekepeAuth}/`, {
      social: "opekepe",
      userCode: code,
      isKyd: scope === OPEKEPE_SYNC_SCOPE.KYD,
    });
    const opekepeSyncData = OpekepeDto.userSync(response.data.data);
    return { data: opekepeSyncData };
  } catch (error) {
    return { error };
  }
};

const getSyncedAccount = async (userId) => {
  try {
    const response = await axiosInstance.get(
      `${URLS.opekepeAuth}/?user_id=${userId}`
    );
    const opekepeSyncData = OpekepeDto.userSync(response.data.data);
    return { data: opekepeSyncData };
  } catch (error) {
    return { error };
  }
};
const checkSyncUser = async (userId) => {
  try {
    const response = await axiosInstance.get(
      `${URLS.opekepeAuth}/is-connected/?user_id=${userId}`
    );
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};
const syncFields = async ({ userId, scope, year }) => {
  try {
    const isKyd = scope === OPEKEPE_SYNC_SCOPE.KYD;
    const response = await axiosInstance.post(
      `${URLS.opekepe}/applications/sync${isKyd ? "-kyd" : ""}/`,
      {
        year,
        initial: false,
        userId,
      }
    );
    const syncedFieldData = OpekepeDto.fieldsSync(response.data);
    return { data: syncedFieldData };
  } catch (error) {
    return { error };
  }
};

const importFields = async ({
  userId,
  year,
  fieldIds,
  removedFieldIds,
  spdFieldIds,
}) => {
  const response = await axiosInstance.post(
    `${URLS.opekepe}/applications/import-fields/`,
    { year, userId, fieldIds, removedFieldIds, spdFieldIds }
  );
  return response.data;
};

const fetchFarmerFields = async ({ userId, year }) => {
  const response = await axiosInstance.get(
    `${URLS.opekepe}/applications/fields/`,
    { params: { year, user_id: userId } }
  );
  return OpekepeDto.farmerFieldsList(response.data);
};

const checkSyncFieldsStatus = async (year) => {
  try {
    const response = await axiosInstance.get(`${URLS.opekepe}/applications/`);

    const syncedFieldStatus = OpekepeDto.checkFieldsSynced(response.data, year);
    return { data: syncedFieldStatus };
  } catch (error) {
    return { error };
  }
};

const getFarmerSyncStatus = async (year) => {
  try {
    const { data } = await axiosInstance.get(`${URLS.opekepe}/applications/`);
    const farmerSyncStatus = OpekepeDto.checkFarmerSyncStatus(data, year);
    return { data: farmerSyncStatus };
  } catch (error) {
    return { error };
  }
};

const getFieldOpekepeInfo = async ({ year, id }) => {
  try {
    const params = { year, field_id: id };
    const response = await axiosInstance.get(
      `${URLS.opekepe}/application-fields/`,
      { params }
    );
    const fieldOpekepeInfo = OpekepeDto.fieldOpekepeInfo(response.data);
    return { data: fieldOpekepeInfo };
  } catch (error) {
    return { error };
  }
};

const getFarmerApplications = async () => {
  try {
    const { data } = await axiosInstance.get(`${URLS.opekepe}/applications/`);
    const applications = OpekepeDto.parseFarmerApplications(data);
    return applications;
  } catch (error) {
    return { error };
  }
};

const getFarmerApplication = async (appId) => {
  try {
    const { data } = await axiosInstance.get(
      `${URLS.opekepe}/applications/${appId}`
    );
    const application = OpekepeDto.parseFarmerApplication(data);
    return application;
  } catch (error) {
    return { error };
  }
};

const fetchCultivars = async () => {
  try {
    const response = await axiosInstance.get(`${URLS.opekepe}/cultivars/`);
    return { data: response.data };
  } catch (error) {
    return { error };
  }
};

const fetchApplicationYears = async () => {
  try {
    const response = await axiosInstance.get(
      `${URLS.opekepe}/opekepe-sync-status/`
    );
    const applicationYearsData = OpekepeDto.parseApplicationYears(
      response.data
    );
    return { data: applicationYearsData };
  } catch (error) {
    return { error };
  }
};

const fetchSubsidyData = async (userId) => {
  try {
    const response = await axiosInstance.get(
      `${URLS.opekepe}/subsidy-calculation/?user_id=${userId}`
    );
    const finalResponse = SubsidyReportDto.setup(response.data);
    return finalResponse;
  } catch (error) {
    throw new Error(error.message);
  }
};

const fetchInflowOutflowData = async (userId, year, fileType = "json") => {
  try {
    const url = `${URLS.opekepe}/farmer-diary-report/`;
    const params = constructSearchParams({
      userId,
      year,
      formatType: fileType,
    });
    const responseType = fileType === "json" ? "json" : "blob";

    const res = await axiosInstance.get(url, {
      params,
      responseType,
    });
    const data = InflowOutflowDto.fromApi(res, fileType);
    return data;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const opekepeApi = {
  syncAccount,
  syncFields,
  importFields,
  fetchFarmerFields,
  checkSyncFieldsStatus,
  getFarmerSyncStatus,
  getFarmerApplications,
  getFarmerApplication,
  checkSyncUser,
  getSyncedAccount,
  fetchFieldInfo: getFieldOpekepeInfo,
  fetchCultivars,
  fetchApplicationYears,
  fetchSubsidyData,
  fetchInflowOutflowData,
};
